/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CosmosEnumEdsGradients, EdsIconsEnum, SectionHeadingFragment } from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CosmosButtonProps,
	CosmosTextVariantEnum,
	CosmosVariableRichText,
	EdsIcon,
	EdsIconType,
	TitleDescStack,
} from "@components/elements";
import { CosmosStackProps } from "@components/types";
import { css, cva, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

export enum SectingHeadingSizesEnum {
	md = "656px",
	lg = "1175px",
}
export type SectingHeadingSizes = keyof typeof SectingHeadingSizesEnum;

export interface SectionHeadingProps
	extends Omit<SectionHeadingFragment, "buttons" | "title" | "description" | "__typename"> {
	__typename?: "CosmosElementSectionHeading" | "CosmosElementIconSectionHeading";
	icon?: EdsIconsEnum | null;
	iconGradient?: CosmosEnumEdsGradients | null;
	title?: CosmosVariableRichText | null;
	desc?: string | null;
	buttons?: CosmosButtonProps[];
	size?: SectingHeadingSizes;
	titleDescStack?: CosmosStackProps;
	mobileTextAlignStart?: boolean;
	buttonAlignLeft?: boolean | null;
}

const styled = cva({
	base: {
		width: "full",
		lgDown: {
			flexDirection: "column",
			gap: "eds.dimension.32",
		},

		smDown: {
			gap: "eds.dimension.24",
			"& .cosmos-text.paragraph": {
				textStyle: "paragraphMD",
			},
		},
	},
	variants: {
		__typename: {
			CosmosElementIconSectionHeading: {
				"&&": {
					gap: "eds.dimension.24",
					"& .section-heading, & .title-desc-stack": {
						gap: "eds.dimension.16",
					},
					"& svg": {
						height: "eds.dimension.80!important", //override EDS style
						width: "eds.dimension.80!important",
					},
					"& .button-list": {
						paddingBlock: "0",
					},
				},
			},
			CosmosElementSectionHeading: {},
			undefined: {},
		},
		// Some components want 1 step smaller textStyle for headings
		compressedTitle: {
			true: {
				lgDown: {
					flexDirection: "row",
				},
				smDown: {
					"&.cosmos-rich-text.heading": {
						textStyle: "headingLG",
					},
				},
			},
			false: {
				mdDown: {
					"& .cosmos-text.heading": {
						textStyle: "heading2XL",
					},
				},
				smDown: {
					"& .cosmos-text.heading": {
						textStyle: "headingXL",
					},
				},
			},
		},
	},
});

export const SectionHeading = (props: SectionHeadingProps | undefined): React.JSX.Element | null => {
	if (!props) return null;

	const {
		__typename,
		icon,
		iconGradient,
		title,
		desc,
		compressedTitle,
		centeredVerticalStack,
		buttons = [],
		size = "md",
		titleGradient,
		titleDescStack,
		mobileTextAlignStart = false,
		buttonAlignLeft = false,
	} = props;

	const validDesc = Boolean(desc);

	if (!title?.text && !validDesc && !buttons.length) return null;

	const stackTitle: CosmosVariableRichText = {
		...title,
		variant: compressedTitle ? CosmosTextVariantEnum.headingLG : CosmosTextVariantEnum.heading2XL,
		as: title?.as || "h2",
	};
	const stackDesc: CosmosVariableRichText = {
		html: desc,
		variant: CosmosTextVariantEnum.paragraphMD,
	};

	const cosmosButtons = buttons.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
			variant: button.variant || "outline", //force default to outline
		};
	});

	const maxWidth = SectingHeadingSizesEnum[size] || SectingHeadingSizesEnum.md;

	let alignItems = "end";
	if (centeredVerticalStack) {
		alignItems = "center";
	} else if (buttonAlignLeft) {
		alignItems = "start";
	}
	const textAlign = centeredVerticalStack ? "center" : "start";
	const lgDownAlignItems = mobileTextAlignStart || (!centeredVerticalStack && validDesc) ? "start" : "center";
	const lgDownTextAlign =
		centeredVerticalStack || (!compressedTitle && !validDesc && !mobileTextAlignStart) ? "center" : "start";

	return (
		<HStack
			className={cx(
				`section-heading`,
				cx(styled({ __typename, compressedTitle: !!compressedTitle })),
				css({
					textAlign,
					lgDown: { alignItems: lgDownAlignItems, textAlign: lgDownTextAlign },
				}),
			)}
			alignItems={alignItems}
			justifyContent={centeredVerticalStack ? "center" : "space-between"}
			flexDirection={centeredVerticalStack || buttonAlignLeft ? "column" : "row"}
		>
			<VStack alignItems={centeredVerticalStack ? "center" : "flex-start"} gap="eds.dimension.24">
				{icon ? <EdsIcon size="xl" icon={icon as EdsIconType} gradient={iconGradient} /> : null}
				<TitleDescStack
					title={stackTitle}
					description={stackDesc}
					stack={{ ...titleDescStack, gap: titleDescStack?.gap || "eds.dimension.24" }}
					gradient={titleGradient}
					className={css({ maxWidth, lgDown: { maxWidth: "full" } })}
				/>
			</VStack>
			{cosmosButtons?.length ? (
				<ButtonList
					className={css({
						h: "full",
						lg: {
							paddingBlock: buttonAlignLeft || centeredVerticalStack ? "eds.dimension.12" : "0",
						},
					})}
					buttons={cosmosButtons}
				/>
			) : null}
		</HStack>
	);
};
